/**
 * Shopping list → plain-text export.
 *
 * Used by the Share button on the shopping list page so users can send
 * their list via WhatsApp / Messages / Email or copy/download it. We
 * deliberately produce **plain text** (no markdown) for maximum
 * compatibility with messaging apps that strip formatting.
 */

import { DEPARTMENT_ORDER, type Department } from "./departments";
import type { ShoppingItem } from "./shoppingList";

export type FormatOptions = {
  /** Include items the user has already ticked off. Defaults to false. */
  includeChecked?: boolean;
};

/** Render a single item line: "2 cloves garlic, minced". */
export const formatShoppingListItemLine = (item: ShoppingItem): string => {
  const qty = item.quantity?.trim() ?? "";
  const unit = item.unit?.trim() ?? "";
  const name = item.name?.trim() ?? "";
  if (!name) return "";

  const qtyUnit = [qty, unit].filter(Boolean).join(" ");
  const head = qtyUnit ? `${qtyUnit} ${name}` : name;
  const note = item.note?.trim();
  return note ? `${head}, ${note}` : head;
};

/**
 * Build a shareable plain-text shopping list grouped by department.
 *
 *   🛒 {menuTitle} — Shopping list
 *
 *   PRODUCE
 *   • 2 lemons
 *   • 500 g spinach
 *
 *   DAIRY & EGGS
 *   • 250 ml milk
 *
 *   Made with CleanPlate
 */
export const formatShoppingListText = (
  menuTitle: string,
  items: ShoppingItem[],
  options: FormatOptions = {},
): string => {
  const { includeChecked = false } = options;
  const visible = items.filter((it) => (includeChecked ? true : !it.checked));

  const grouped = new Map<Department, ShoppingItem[]>();
  for (const dept of DEPARTMENT_ORDER) grouped.set(dept, []);
  for (const it of visible) {
    if (!grouped.has(it.department)) grouped.set(it.department, []);
    grouped.get(it.department)!.push(it);
  }

  const lines: string[] = [];
  const title = menuTitle?.trim() || "Shopping list";
  lines.push(`🛒 ${title} — Shopping list`);
  lines.push("");

  let wroteAny = false;
  for (const dept of DEPARTMENT_ORDER) {
    const deptItems = (grouped.get(dept) || [])
      .slice()
      .sort((a, b) => a.position - b.position);
    if (deptItems.length === 0) continue;
    wroteAny = true;
    lines.push(dept.toUpperCase());
    for (const it of deptItems) {
      const line = formatShoppingListItemLine(it);
      if (line) lines.push(`• ${line}`);
    }
    lines.push("");
  }

  if (!wroteAny) {
    lines.push("(no items)");
    lines.push("");
  }

  lines.push("Made with CleanPlate");
  return lines.join("\n");
};

/**
 * Strip a single ingredient display name down to a clean search term suitable
 * for a supermarket search box (Sainsbury's, Tesco, Ocado, Amazon Fresh, …).
 *
 * Removes parentheticals, trailing notes after a comma, and a small set of
 * leading prep / size descriptors — but keeps colour qualifiers that change
 * identity (red onion, black pepper, white wine vinegar).
 */
const STRIP_LEADING_DESCRIPTORS = new Set([
  "fresh", "dried", "ripe", "large", "small", "medium", "extra",
  "finely", "roughly", "coarsely", "whole", "raw", "cooked",
  "organic", "free-range", "free", "range",
]);

export const cleanIngredientForSearch = (rawName: string): string => {
  if (!rawName) return "";
  // Drop parentheticals: "(about 2 large)"
  let name = rawName.replace(/\s*\([^)]*\)\s*/g, " ");
  // Drop trailing note after first comma: "lemon, cut into wedges"
  const comma = name.indexOf(",");
  if (comma !== -1) name = name.slice(0, comma);
  name = name.trim().replace(/\s+/g, " ");
  if (!name) return "";

  // Strip leading descriptor words.
  const tokens = name.split(" ");
  while (
    tokens.length > 1 &&
    STRIP_LEADING_DESCRIPTORS.has(tokens[0].toLowerCase())
  ) {
    tokens.shift();
  }
  const cleaned = tokens.join(" ").trim();
  if (!cleaned) return "";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

export type SearchExportOptions = FormatOptions & {
  /** Join items with ", " on a single line (for single-search-box pastes). */
  joinAsCsv?: boolean;
};

/**
 * Build a "copy-for-online-shop" plain-text list: one clean ingredient per
 * line (or CSV), no quantities, units, departments, bullets or notes.
 * Deduplicates after cleaning so "red onion" and "red onion, sliced"
 * collapse to a single search term.
 */
export const formatShoppingListForSearch = (
  items: ShoppingItem[],
  options: SearchExportOptions = {},
): string => {
  const { includeChecked = false, joinAsCsv = false } = options;
  const visible = items.filter((it) => (includeChecked ? true : !it.checked));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of visible) {
    const cleaned = cleanIngredientForSearch(it.name);
    if (!cleaned) continue;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
  }
  return joinAsCsv ? out.join(", ") : out.join("\n");
};

/** Filename-safe slug for downloads. */
export const slugifyForFilename = (input: string): string => {
  const base = (input || "shopping-list")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base || "shopping-list";
};
